import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, BellOff, Check, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PROFILE_KEY = "hireds_profile_v1";
const PUSH_DISMISSED_KEY = "hireds_push_dismissed_v1";
const PUSH_STATE_KEY = "hireds_push_subscribed_v1";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function loadProfile() {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || "null"); } catch { return null; }
}

export default function PushPermissionCard() {
  const [profile, setProfile] = useState(null);
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setProfile(loadProfile());
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (ok) {
      setSubscribed(localStorage.getItem(PUSH_STATE_KEY) === "yes" || Notification.permission === "granted");
    }
    setHidden(localStorage.getItem(PUSH_DISMISSED_KEY) === "yes");
  }, []);

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.register("/push-sw.js");
      const { data } = await axios.get(`${BACKEND}/api/push/public-key`);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const raw = sub.toJSON();
      const body = {
        endpoint: raw.endpoint,
        keys: raw.keys,
        qualification: profile?.qualification || null,
        state: profile?.state || null,
        department: profile?.department || null,
      };
      await axios.post(`${BACKEND}/api/push/subscribe`, body);
      localStorage.setItem(PUSH_STATE_KEY, "yes");
      setSubscribed(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Subscription failed";
      setError(typeof msg === "string" ? msg : "Subscription failed");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(PUSH_DISMISSED_KEY, "yes");
    setHidden(true);
  };

  // Only show if: supported, has profile, not already subscribed, not dismissed.
  if (!supported || !profile || subscribed || hidden) return null;

  return (
    <section
      data-testid="push-permission-card"
      className="max-w-3xl mx-auto mt-4 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50 p-4 md:p-5"
    >
      <div className="flex items-start gap-3">
        <span className="inline-grid place-items-center w-10 h-10 rounded-md bg-blue-600 text-white shrink-0">
          <Bell className="w-5 h-5" aria-hidden="true" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Instant alerts</p>
          <p className="text-sm md:text-base font-extrabold text-slate-900 leading-snug" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            Get notified the moment a {profile.department || "matching"} vacancy drops
          </p>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            One tap → browser pings you within seconds of a new notification. No emails, no spam. Turn it off anytime in browser settings.
          </p>
          {error && (
            <p className="text-xs text-red-600 mt-2" data-testid="push-error">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={enable}
              disabled={busy}
              data-testid="push-enable"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-60 transition"
            >
              {busy ? "Subscribing…" : (<><Check className="w-4 h-4" /> Enable alerts</>)}
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="push-dismiss"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-slate-600 hover:text-slate-900 text-sm font-semibold"
            >
              <X className="w-3.5 h-3.5" /> Not now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PushSubscribedBadge() {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    setShown(typeof window !== "undefined" && localStorage.getItem(PUSH_STATE_KEY) === "yes");
  }, []);
  if (!shown) return null;
  return (
    <div
      className="max-w-3xl mx-auto mt-3 text-xs text-blue-700 flex items-center gap-2 justify-center"
      data-testid="push-subscribed-badge"
    >
      <BellOff className="w-3.5 h-3.5" aria-hidden="true" />
      Alerts enabled — we'll ping you when a matching vacancy lands.
    </div>
  );
}
